import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api.js';

// The renewal calendar. The one thing David currently keeps in his head.
//
// Every bound policy with a future expiration, ordered by expiration, read
// from Zoho Deals (the book). Rows TRACON holds no record of still appear —
// a policy TRACON does not know about is the row most worth seeing.
//
// This page RENDERS. It derives nothing (CLAUDE.md, One Fact One Derivation).
// `renewal_started` and `days_until_expiration` are computed server-side in
// buildPipeline; the bands below are presentation only.

const BANDS = [
  { key: 'inside14', label: 'Inside 14 days', max: 14 },
  { key: 'inside30', label: 'Inside 30 days', max: 30 },
  { key: 'inside60', label: 'Inside 60 days', max: 60 },
  { key: 'inside90', label: 'Inside 90 days', max: 90 },
  { key: 'inside120', label: 'Inside 120 days', max: 120 },
  { key: 'beyond', label: 'Beyond 120 days', max: Infinity },
];

function bandOf(days) {
  return BANDS.find((b) => days <= b.max) || BANDS[BANDS.length - 1];
}

// The tail cell. `aircraft` comes from the Deal's Aircraft_on_Policy subform,
// and the Zoho LIST api does not return subform data (CLAUDE.md), so it is
// empty on most rows. deal_name is the honest fallback — David names deals by
// tail ("N9364P 2025-2026"). Suppressed when it just repeats the account name.
function tailOf(r) {
  if (r.aircraft) return r.aircraft;
  if (r.deal_name && r.deal_name !== r.named_insured) return r.deal_name;
  return '';
}

function Row({ r }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border/60 px-3.5 py-2.5 last:border-b-0">
      <span className="w-14 shrink-0 tabular-nums text-sm font-semibold text-text">
        {r.days_until_expiration}d
      </span>
      <span className="w-24 shrink-0 tabular-nums text-sm text-text-muted">
        {r.policy_expiration}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm text-text">
        <Link to={`/account/${r.deal_id}/deal/${r.deal_id}`} className="hover:underline">
          {r.named_insured || r.deal_name || r.deal_id}
        </Link>
      </span>
      <span className="shrink-0 text-sm text-text-muted">{tailOf(r)}</span>
      {r.renewal_started ? (
        <span className="shrink-0 rounded-full bg-border/40 px-2 py-0.5 text-xs text-text-muted">
          Renewal started
        </span>
      ) : (
        <span className="shrink-0 rounded-full bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
          Not started
        </span>
      )}
    </div>
  );
}

export default function Renewals() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [onlyNotStarted, setOnlyNotStarted] = useState(true);
  // The back half of the book is most of the list and none of it is due. It is
  // collapsed, never filtered out — the whole point is that nothing is hidden.
  const [showBeyond, setShowBeyond] = useState(false);

  const refresh = useCallback(() => {
    api
      .get(`/api/tracon/renewal-horizon?_t=${Date.now()}`)
      .then((d) => { setData(d); setError(''); })
      .catch((err) => setError(err.body?.error || err.message || 'Load failed'));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const rows = (data?.rows || []).filter((r) => (onlyNotStarted ? !r.renewal_started : true));

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl font-semibold text-text">Renewals</h2>
        {data ? (
          <p className="text-sm text-text-muted tabular-nums">
            {data.count} bound {data.count === 1 ? 'policy' : 'policies'}, {data.not_started} not started
            {data.excluded_cancelled > 0 ? ` (${data.excluded_cancelled} cancelled, excluded)` : ''}
          </p>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-text-muted">
        Every bound policy by expiration date. Read from the CRM, so policies TRACON has never
        touched are on this list too. Started means a deal already exists for the next term.
      </p>

      <label className="mt-3 flex w-fit items-center gap-2 text-sm text-text-muted">
        <input
          type="checkbox"
          checked={onlyNotStarted}
          onChange={(e) => setOnlyNotStarted(e.target.checked)}
          className="h-4 w-4 accent-current"
        />
        Only show renewals not started
      </label>

      {error ? (
        <p className="mt-6 text-sm text-danger">{error}</p>
      ) : data === null ? (
        <p className="mt-6 text-sm text-text-muted">Reading the book…</p>
      ) : rows.length === 0 ? (
        <div className="mt-6 rounded-card border border-dashed border-border p-6 text-sm text-text-muted">
          {onlyNotStarted
            ? 'Every bound policy has a renewal underway.'
            : 'No bound policies with a future expiration.'}
        </div>
      ) : (
        <div className="mt-4 space-y-5">
          {BANDS.map((band) => {
            const inBand = rows.filter((r) => bandOf(r.days_until_expiration).key === band.key);
            if (inBand.length === 0) return null;
            const collapsible = band.key === 'beyond';
            const open = !collapsible || showBeyond;
            return (
              <section key={band.key}>
                {collapsible ? (
                  <button
                    type="button"
                    onClick={() => setShowBeyond((v) => !v)}
                    className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted hover:text-text"
                  >
                    {open ? 'Hide' : 'Show'} {band.label}{' '}
                    <span className="tabular-nums font-normal">({inBand.length})</span>
                  </button>
                ) : (
                  <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
                    {band.label} <span className="tabular-nums font-normal">({inBand.length})</span>
                  </h3>
                )}
                {open ? (
                  <div className="rounded-card border border-border bg-surface">
                    {inBand.map((r) => <Row key={r.deal_id} r={r} />)}
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
